<template>
	<div class="grid spe-v2">
		<Toast />
		<div class="col-12">
			<Breadcrumb :home="breadcrumbHome" :model="breadcrumbItems" />
		</div>

		<div class="col-12">
			<div class="content-wrapper">
				<div class="content-header flex justify-content-between align-items-center flex-nowrap gap-2">
					<span>SPE Report</span>
					<Button v-if="isUserPurchasing" label="Export Excel" icon="pi pi-file-excel"
						class="p-button-secondary min-w-min px-3 py-2" style="min-width: unset; width: auto;"
						:loading="exporting" :disabled="exporting || !company" @click="exportToExcel" />
				</div>
				<div class="content-body">
					<TabView v-model:activeIndex="activeTab" @tab-change="onTabChange">
						<TabPanel header="Outstanding">
							<SpeV2Outstanding ref="outstandingRef" :currentUser="currentUser" :company="company"
								:vendorId="vendorId" @view-detail="openDetail" @review-pdf="openPdfReview"
								@refresh="refreshLists" />
						</TabPanel>
						<TabPanel v-if="isUserPurchasing" header="Pending CAPA">
							<SpeV2PendingCapa ref="pendingCapaRef" :currentUser="currentUser" :company="company"
								:vendorId="vendorId" :on-review-pdf="openPdfReview" @view-detail="openDetail"
								@review-pdf="openPdfReview" />
						</TabPanel>
						<TabPanel v-if="isUserPurchasing" header="Pending CAPA Proxy">
							<SpeV2PendingCapaProxy ref="pendingCapaProxyRef" :currentUser="currentUser"
								:company="company" :vendorId="vendorId" :on-review-pdf="openPdfReview"
								@view-detail="openDetail" @review-pdf="openPdfReview" />
						</TabPanel>
						<TabPanel header="History">
							<SpeV2History ref="historyRef" :currentUser="currentUser" :company="company"
								:vendorId="vendorId" :on-review-pdf="openPdfReview"
								@view-detail="(id) => openDetail(id, 'history')" @review-pdf="openPdfReview" />
						</TabPanel>
						<TabPanel v-if="isUserPurchasing" header="Pending Dismiss">
							<SpeV2PendingDismiss ref="pendingDismissRef" :currentUser="currentUser" :company="company"
								:vendorId="vendorId" :on-review-pdf="openPdfReview" @view-detail="openDetail"
								@review-pdf="openPdfReview" />
						</TabPanel>
						<TabPanel v-if="isUserPurchasing" header="Dismiss">
							<SpeV2Dismiss ref="dismissRef" :currentUser="currentUser" :company="company"
								:vendorId="vendorId" :on-review-pdf="openPdfReview" @view-detail="openDetail"
								@review-pdf="openPdfReview" />
						</TabPanel>
					</TabView>
				</div>
			</div>
		</div>

		<Dialog v-model:visible="showDetail" :modal="true" appendTo="body" :showHeader="false"
			:style="detailDialogStyle" :draggable="false" :resizable="false" :maximizable="false"
			class="spe-v2-detail-dialog" :baseZIndex="100" :contentStyle="{ padding: 0, height: '100%' }"
			@show="() => syncSpeV2DialogLayer('.spe-v2-detail-dialog', 1200)">
			<div v-if="detailLoading" class="flex justify-content-center align-items-center" style="min-height:200px">
				<ProgressSpinner />
			</div>
			<div v-else-if="detailData" class="spe-detail-shell">

				<!-- ── HERO HEADER ── -->
				<div class="spe-detail-hero" :class="'spe-detail-hero--' + detailData.status">
					<div class="spe-detail-hero__left">
						<div class="spe-detail-hero__vendor">
							<i class="pi pi-building mr-2" />
							{{ detailVendorName || '-' }}
						</div>
						<div class="spe-detail-hero__meta">
							<span class="spe-detail-meta-badge">
								<i class="pi pi-calendar mr-1" />{{ detailData.year }} · S{{ detailData.semester }}
							</span>
							<span class="spe-detail-meta-badge">
								<i class="pi pi-tag mr-1" />{{ detailData.category || '-' }}
							</span>
							<span class="spe-detail-meta-badge">
								<i class="pi pi-cog mr-1" />{{ detailData.method || '-' }}
							</span>
							<span class="spe-detail-meta-badge">
								<i class="pi pi-building mr-1" />{{ detailCompanyName }}
							</span>
						</div>
					</div>
					<div class="spe-detail-hero__score-wrap">
						<div class="spe-detail-score-ring"
							:class="scoreClassFor(detailData.supplier_score, detailData.total_result)">
							<span class="spe-detail-score-ring__value">{{ detailData.supplier_score ?? '-' }}</span>
							<span class="spe-detail-score-ring__label">Supplier Score</span>
						</div>
					</div>
					<div class="spe-detail-hero__status-wrap">
						<span class="spe-detail-status-pill" :class="'spe-detail-status-pill--' + detailData.status">
							<i class="pi mr-1"
								:class="detailData.status === 'P' ? 'pi-check-circle' : detailData.status === 'R' ? 'pi-times-circle' : detailData.status === 'B' ? 'pi-clock' : 'pi-exclamation-circle'" />
							{{ detailStatusLabel }}
						</span>
					</div>
					<button class="spe-detail-hero__close p-link" @click="showDetail = false">
						<i class="pi pi-times" />
					</button>
				</div>

				<!-- ── SCROLLABLE BODY ── -->
				<div class="spe-detail-scroll pb-4">

					<!-- ── STAT CARDS ── -->
					<div class="spe-detail-stats">
						<!-- <div class="spe-detail-stat-card">
							<span class="spe-detail-stat-card__label">Total Result</span>
							<span class="spe-detail-stat-card__value spe-detail-stat-card__value--primary">
								{{ detailData.total_result ?? '-' }}
							</span>
						</div> -->
						<div class="spe-detail-stat-card">
							<span class="spe-detail-stat-card__label">% Score</span>
							<span class="spe-detail-stat-card__value">{{ detailData.percent_score ?? '-' }}</span>
						</div>
						<div class="spe-detail-stat-card">
							<span class="spe-detail-stat-card__label">Jumlah Bulan</span>
							<span class="spe-detail-stat-card__value">{{ detailData.jumlah_bulan ?? '-' }}</span>
						</div>
						<div class="spe-detail-stat-card">
							<span class="spe-detail-stat-card__label">Quality Capability</span>
							<span class="spe-detail-stat-card__value">{{ detailData.total_quality_capability ?? '-'
							}}</span>
						</div>
						<div class="spe-detail-stat-card">
							<span class="spe-detail-stat-card__label">Delivery Capability</span>
							<span class="spe-detail-stat-card__value">{{ detailData.total_delivery_capability ?? '-'
							}}</span>
						</div>
						<div class="spe-detail-stat-card">
							<span class="spe-detail-stat-card__label">Quality Achieve</span>
							<span class="spe-detail-stat-card__value">{{ detailData.total_quality_achieve ?? '-'
							}}</span>
						</div>
						<div class="spe-detail-stat-card">
							<span class="spe-detail-stat-card__label">Delivery Achieve</span>
							<span class="spe-detail-stat-card__value">{{ detailData.total_delivery_achieve ?? '-'
							}}</span>
						</div>
					</div>

					<!-- ── DETAIL TABLE ── -->
					<div v-if="detailData.details && detailData.details.length" class="spe-detail-body">
						<div class="spe-detail-section-title">
							<i class="pi pi-table mr-2" />Detail Categories
						</div>
						<DataTable :value="flatDetailRows" class="p-datatable-sm spe-v2-detail-datatable">
							<Column header="Category" field="category" headerClass="spe-v2-detail-col-category"
								bodyClass="spe-v2-detail-col-category">
								<template #body="{ data }">
									<Tag :value="data.category" :severity="categorySeverity(data.category)" />
								</template>
							</Column>
							<Column header="Receiving Method" field="receiving_method_name"
								headerClass="spe-v2-detail-col-receiving" bodyClass="spe-v2-detail-col-receiving">
								<template #body="{ data }">
									<span class="spe-v2-cell-wrap">{{ data.receiving_method_name || '-' }}</span>
								</template>
							</Column>
							<Column header="Method" field="method" headerClass="spe-v2-detail-col-method"
								bodyClass="spe-v2-detail-col-method">
								<template #body="{ data }">
									<span class="spe-v2-cell-wrap">{{ data.method || '-' }}</span>
								</template>
							</Column>
							<Column header="Supplier Name" field="supplier_name"
								headerClass="spe-v2-detail-col-supplier" bodyClass="spe-v2-detail-col-supplier">
								<template #body="{ data }">
									<span class="spe-v2-cell-wrap">{{ data.supplier_name || '-' }}</span>
								</template>
							</Column>
							<Column v-for="month in monthColumns" :key="month" :header="month" :field="month"
								headerClass="spe-v2-detail-col-month" bodyClass="spe-v2-detail-col-month">
								<template #body="{ data: row }">
									<span v-if="row[month] != null" class="font-semibold">{{ row[month] }}</span>
									<span v-else class="text-color-secondary">-</span>
								</template>
							</Column>
						</DataTable>
					</div>

					<!-- ── ACKNOWLEDGE REASON (read-only: supplier, or purchasing History) ── -->
					<div v-if="showAckReasonReadonly" class="spe-detail-acknowledge-readonly">
						<div class="spe-detail-section-title">
							<i class="pi pi-comment mr-2" />Reason Acknowledge
						</div>
						<Textarea :modelValue="detailData.acknowledge_reason || ''" rows="3" class="w-full" disabled
							readonly />
					</div>

					<div v-if="showAckAttachmentReadonly" class="spe-detail-acknowledge-readonly">
						<div class="spe-detail-section-title">
							<i class="pi pi-paperclip mr-2" />Attachment
						</div>
						<div class="flex align-items-center flex-wrap gap-2">
							<span
								class="spe-detail-acknowledge-readonly__text spe-detail-acknowledge-readonly__text--inline mr-2">
								{{ detailData.acknowledge_attachment_original_name }}
							</span>
							<Button :label="loadingAckAttachment ? 'Loading...' : 'View Document'" icon="pi pi-eye"
								class="p-button-outlined p-button-sm" :disabled="loadingAckAttachment"
								@click="handleViewAcknowledgeAttachment" />
							<Button :label="loadingAckAttachment ? 'Loading...' : 'Download'" icon="pi pi-download"
								class="p-button-text p-button-sm" :disabled="loadingAckAttachment"
								@click="handleDownloadAcknowledgeAttachment" />
						</div>
					</div>

					<!-- ── DISMISS JUSTIFICATION + DOCUMENT (PD / D) ── -->
					<div v-if="showDismissDetailSection" class="spe-detail-acknowledge-readonly">
						<div class="spe-detail-section-title">
							<i class="pi pi-ban mr-2" />Dismiss Justification
						</div>
						<p class="spe-detail-acknowledge-readonly__text">
							{{ detailData.dismiss_approve_reason || '-' }}
						</p>
						<div v-if="detailData.dismiss_requested_at" class="mt-2 text-600 text-sm">
							Requested At: {{ formatDismissDetailDate(detailData.dismiss_requested_at) }}
						</div>
						<div v-if="detailData.dismiss_decided_at" class="mt-1 text-600 text-sm">
							Decided At: {{ formatDismissDetailDate(detailData.dismiss_decided_at) }}
						</div>
					</div>

					<div v-if="showDismissDetailSection && detailData.dismiss_approve_attachment_original_name"
						class="spe-detail-acknowledge-readonly">
						<div class="spe-detail-section-title">
							<i class="pi pi-paperclip mr-2" />Supporting Document
						</div>
						<div class="flex align-items-center flex-wrap gap-2">
							<span
								class="spe-detail-acknowledge-readonly__text spe-detail-acknowledge-readonly__text--inline mr-2">
								{{ detailData.dismiss_approve_attachment_original_name }}
							</span>
							<Button :label="loadingDismissAttachment ? 'Loading...' : 'View Document'" icon="pi pi-eye"
								class="p-button-outlined p-button-sm" :disabled="loadingDismissAttachment"
								@click="handleViewDismissAttachment" />
							<Button :label="loadingDismissAttachment ? 'Loading...' : 'Download'" icon="pi pi-download"
								class="p-button-text p-button-sm" :disabled="loadingDismissAttachment"
								@click="handleDownloadDismissAttachment" />
						</div>
					</div>

					<!-- ── ACKNOWLEDGE INPUT (supplier SPBI Bad/Poor, PUR CAPA Proxy Bad/Poor, or History Confirm Exc/Avg/Good) ── -->
					<div
						v-if="showSupplierAckInput || showPurchasingProxyAckInput || showPurchasingHistoryAckInput"
						class="spe-detail-acknowledge">
						<label class="spe-detail-acknowledge__label" for="speAcknowledgeReason">
							Reason Acknowledge
							<span v-if="!showPurchasingHistoryAckInput" class="spe-detail-acknowledge__required">*</span>
							<span v-else class="spe-detail-acknowledge__optional">(optional)</span>
						</label>
						<Textarea id="speAcknowledgeReason" v-model="acknowledgeReason" rows="3" maxlength="500"
							class="w-full" placeholder="Masukkan alasan acknowledge..."
							:disabled="acknowledging || completingProxy || confirming" />
						<small class="spe-detail-acknowledge__hint">
							{{ acknowledgeReason.trim().length }}/500
							<span v-if="!showPurchasingHistoryAckInput"> · minimum {{ ACKNOWLEDGE_REASON_MIN_LENGTH }} words for Bad / Poor</span>
						</small>

						<label class="spe-detail-acknowledge__label mt-3" for="speAcknowledgeAttachment">
							Attachment (PDF)
							<span class="spe-detail-acknowledge__optional">(optional)</span>
						</label>
						<FileUpload ref="acknowledgeFileUpload" id="speAcknowledgeAttachment" mode="basic" name="file"
							accept=".pdf,application/pdf" chooseLabel="Choose File" class="w-full"
							:disabled="acknowledging || completingProxy || confirming" @select="onAcknowledgeFileSelect"
							@remove="onAcknowledgeFileRemove" @clear="onAcknowledgeFileRemove" />
						<small v-if="acknowledgeFileName" class="spe-detail-acknowledge__hint">
							Selected: {{ acknowledgeFileName }}
						</small>
					</div>

				</div><!-- /spe-detail-scroll -->

				<!-- ── ACTION FOOTER ── -->
				<div class="spe-detail-footer">
					<div class="spe-detail-footer__left">
						<Button v-if="isUserPurchasing && (detailData.status === 'O' || detailData.status === 'R')"
							:label="submitting ? 'Sending...' : 'Send to Supplier'" icon="pi pi-send"
							class="p-button-success mr-2" :disabled="submitting || !selectedCategories.length"
							@click="handleSubmit" />
						<Button v-if="canDismissDetail" :label="dismissing ? 'Dismissing...' : 'Dismiss'"
							icon="pi pi-ban" class="p-button-secondary mr-2" :disabled="submitting || dismissing"
							@click="handleDismiss" />
						<Button v-if="canCompleteCapaProxy"
							:label="completingProxy ? 'Completing...' : 'Complete CAPA (Proxy)'"
							icon="pi pi-check-circle" class="p-button-warning mr-2"
							:disabled="completingProxy || !canSubmitCapaProxy" @click="handleCompleteCapaProxy" />
						<Button v-if="canConfirmFromHistory" :label="confirming ? 'Confirming...' : 'Confirm (Proxy)'"
							icon="pi pi-check" class="p-button-success mr-2" :disabled="confirming"
							@click="handleConfirm" />
						<template v-if="isUserSupplier && detailData.status === 'B'">
							<Button :label="acknowledging ? 'Acknowledging...' : 'Acknowledge'" icon="pi pi-check"
								class="p-button-success" :disabled="acknowledging || !canSubmitAcknowledge"
								@click="handleAcknowledge" />
						</template>
						<span v-if="detailData.status === 'PD'" class="text-orange-600 font-medium">
							Waiting for dismiss approval via email
						</span>
					</div>
					<div class="spe-detail-footer__right">
						<Button :label="loadingPdfReview ? 'Generating...' : 'Review PDF'" icon="pi pi-file-pdf"
							class="p-button-secondary mr-2" :disabled="loadingPdfReview" @click="handleReviewPdf" />
						<Button label="Close" icon="pi pi-times" class="p-button-outlined"
							@click="showDetail = false" />
					</div>
				</div>
			</div>
		</Dialog>

		<Dialog v-model:visible="showPdfReview" header="Review PDF" :modal="true" appendTo="body"
			:style="{ width: '85vw' }" :maximizable="true" class="spe-v2-pdf-review-dialog" :baseZIndex="300"
			@show="() => syncSpeV2DialogLayer('.spe-v2-pdf-review-dialog', 1400)" @hide="clearPdfReview">
			<div v-if="loadingPdfReview" class="flex justify-content-center p-6">
				<ProgressSpinner />
			</div>
			<iframe v-else-if="pdfReviewUrl" :src="pdfReviewUrl" class="spe-v2-pdf-preview"
				title="SPE Report PDF Preview" />
			<div v-else class="p-4 text-center text-color-secondary">
				Failed to load PDF preview.
			</div>
			<template #footer>
				<Button label="Download PDF" icon="pi pi-download" class="p-button-success mr-2"
					:disabled="!pdfReviewBase64 || loadingPdfReview" @click="handleDownloadPdfFile" />
				<Button label="Close" icon="pi pi-times" class="p-button-outlined" @click="showPdfReview = false" />
			</template>
		</Dialog>

		<Dialog v-model:visible="showDismissDialog" header="Dismiss Request" :modal="true" appendTo="body"
			:style="{ width: '36rem', maxWidth: '95vw' }" :draggable="false" :resizable="false"
			class="spe-v2-dismiss-dialog" :baseZIndex="2000"
			@show="() => syncSpeV2DialogLayer('.spe-v2-dismiss-dialog', 2000)" @hide="resetDismissForm">
			<p class="text-600 mt-0 mb-3">
				Lengkapi justification dan lampiran. Setelah submit, report akan masuk ke tab
				<strong>Pending Dismiss</strong> menunggu approval Manager.
			</p>
			<div class="flex flex-column gap-3">
				<div>
					<label class="font-semibold block mb-2" for="speDismissJustification">
						Justification <span class="text-red-500">*</span>
					</label>
					<Textarea id="speDismissJustification" v-model="dismissJustification" rows="5" class="w-full"
						autoResize placeholder="Input justification / explanation" :disabled="dismissing"
						maxlength="1000" />
					<small class="text-500">{{ String(dismissJustification || '').trim().length }}/1000</small>
				</div>
				<div>
					<SpeV2PdfDropzone v-model="dismissFile" input-id="speDismissAttachment"
						label="Supporting Document (PDF)" required :disabled="dismissing"
						empty-title="Drop PDF here, or browse" empty-hint="Required · PDF only · max 10 MB"
						@error="onDismissAttachmentError" @clear="dismissFormError = ''" />
				</div>
				<small v-if="dismissFormError" class="p-error">{{ dismissFormError }}</small>
			</div>
			<template #footer>
				<Button label="Cancel" class="p-button-outlined" :disabled="dismissing"
					@click="showDismissDialog = false" />
				<Button label="Submit Request" icon="pi pi-send" class="p-button-danger" :loading="dismissing"
					@click="submitDismissRequest" />
			</template>
		</Dialog>

		<Dialog v-model:visible="showManualEmailDialog" header="Send to Supplier" :modal="true" appendTo="body"
			:style="{ width: '40rem', maxWidth: '95vw' }" :draggable="false" :resizable="false"
			class="spe-v2-manual-email-dialog" :baseZIndex="1600"
			@show="() => syncSpeV2DialogLayer('.spe-v2-manual-email-dialog', 1600)">
			<div class="p-fluid">
				<div class="field mb-3">
					<label class="block mb-2 font-medium">Supplier Type</label>
					<SelectButton v-model="manualEmailSupplierType" :options="manualSupplierTypeOptions"
						optionLabel="label" optionValue="value" :allowEmpty="false" :disabled="submitting" />
					<small class="text-500">Local = Indonesia template · Foreign = English template</small>
				</div>
				<div class="field mb-3">
					<label for="manualEmailTo" class="block mb-2 font-medium">To</label>
					<Chips
						id="manualEmailTo"
						v-model="manualEmailTo"
						separator=","
						:addOnBlur="true"
						placeholder="Masukkan satu atau lebih email To"
						:disabled="submitting"
					/>
				</div>
				<div class="field mb-3">
					<label for="manualEmailCc" class="block mb-2 font-medium">CC</label>
					<Chips
						id="manualEmailCc"
						v-model="manualEmailCc"
						separator=","
						:addOnBlur="true"
						placeholder="Masukkan satu atau lebih email CC"
						:disabled="submitting"
					/>
				</div>
				<div class="field mb-3">
					<label for="manualEmailAttachment" class="block mb-2 font-medium">Attachment</label>
					<InputText id="manualEmailAttachment" :modelValue="manualEmailAttachmentName" disabled />
				</div>
			</div>
			<template #footer>
				<Button label="Cancel" icon="pi pi-times" class="p-button-outlined mr-2" :disabled="submitting"
					@click="showManualEmailDialog = false" />
				<Button :label="submitting ? 'Sending...' : 'Send Email'" icon="pi pi-send" class="p-button-success"
					:disabled="submitting" @click="submitManualEmail" />
			</template>
		</Dialog>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import SimpleCrypto from 'simple-crypto-js';
import AuthService from '../../service/AuthService';
import LovService from '../../service/LovService';
import PoFulfillment from '../../service/PoFulfillment';
import PlanningOrderService from '../../service/PlanningOrderService';
import SpeV2Service from '../../service/SpeV2Service';
import SpeV2Outstanding from './SpeV2Outstanding.vue';
import SpeV2PendingCapa from './SpeV2PendingCapa.vue';
import SpeV2PendingCapaProxy from './SpeV2PendingCapaProxy.vue';
import SpeV2Dismiss from './SpeV2Dismiss.vue';
import SpeV2PendingDismiss from './SpeV2PendingDismiss.vue';
import SpeV2History from './SpeV2History.vue';
import SpeV2PdfDropzone from './SpeV2PdfDropzone.vue';
import { validatePdfAttachment } from './speV2PdfAttachment.js';
import { requiresAcknowledgeReason, ACKNOWLEDGE_REASON_MIN_LENGTH, scoreClassFor, resolveScoreGrade } from './speV2ScoreUtils';
import { collectSpeV2ExportRows, exportSpeV2RowsToExcel } from './speV2ExcelExport';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const currentUser = ref(null);
const company = ref(null);
const companyList = ref(null);
const vendorId = ref(null);
const activeTab = ref(0);
const outstandingRef = ref(null);
const pendingCapaRef = ref(null);
const pendingCapaProxyRef = ref(null);
const dismissRef = ref(null);
const pendingDismissRef = ref(null);
const historyRef = ref(null);
const exporting = ref(false);

const showDetail = ref(false);
const detailLoading = ref(false);
const detailData = ref(null);
const detailSemesterLabel = ref('');
const detailStatusLabel = ref('');
const selectedCategories = ref([]);
const submitting = ref(false);
const dismissing = ref(false);
const showDismissDialog = ref(false);
const dismissJustification = ref('');
const dismissFile = ref(null);
const dismissFormError = ref('');
const completingProxy = ref(false);
const confirming = ref(false);
const acknowledging = ref(false);
const detailEntrySource = ref('');
const acknowledgeReason = ref('');
const acknowledgeFile = ref(null);
const acknowledgeFileName = ref('');
const acknowledgeFileUpload = ref(null);
const loadingAckAttachment = ref(false);
const loadingDismissAttachment = ref(false);
const showPdfReview = ref(false);
const loadingPdfReview = ref(false);
const pdfReviewUrl = ref('');
const pdfReviewBase64 = ref('');
const pdfReviewFileName = ref('');
const showManualEmailDialog = ref(false);
const manualEmailTo = ref([]);
const manualEmailCc = ref([]);
const manualEmailAttachmentName = ref('');
const manualEmailSupplierType = ref('LOCAL');
const manualSupplierTypeOptions = [
	{ label: 'Local (Indonesia)', value: 'LOCAL' },
	{ label: 'Foreign (English)', value: 'FOREIGN' },
];

const normalizeEmailItems = (items) => {
	const seen = new Set();
	return (Array.isArray(items) ? items : [items])
		.flatMap((item) => String(item || '').split(/[,\n;]+/))
		.map((item) => item.trim())
		.filter((item) => item)
		.filter((item) => {
			const key = item.toLowerCase();
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());

const breadcrumbHome = ref({ icon: 'pi pi-home', to: '/dashboard' });
const breadcrumbItems = ref([{ label: 'SPE Report', to: route.fullPath }]);

const isUserSupplier = computed(() => currentUser.value && currentUser.value.leveltenant == 2);
const isUserPurchasing = computed(() => currentUser.value && (currentUser.value.leveltenant == 1 || currentUser.value.leveltenant == 3));

const isPoorAcknowledgeScore = computed(() => {
	if (!detailData.value) return false;
	return requiresAcknowledgeReason(detailData.value.supplier_score, detailData.value.total_result);
});

const acknowledgeReasonWordCount = computed(() =>
	acknowledgeReason.value.trim().split(/\s+/).filter(Boolean).length
);

const canSubmitAcknowledge = computed(() => {
	if (isPoorAcknowledgeScore.value) {
		return acknowledgeReasonWordCount.value >= ACKNOWLEDGE_REASON_MIN_LENGTH;
	}
	return true;
});

/** Supplier SPBI acknowledge input (non-proxy). Hidden for purchasing Pending CAPA. */
const showSupplierAckInput = computed(() =>
	isUserSupplier.value &&
	detailData.value?.status === 'B' &&
	isPoorAcknowledgeScore.value
);

/** Purchasing Pending CAPA Proxy: editable reason + optional upload (Poor/Bad only). */
const showPurchasingProxyAckInput = computed(() =>
	isUserPurchasing.value &&
	detailEntrySource.value === 'pending-capa-proxy' &&
	detailData.value?.status === 'B' &&
	isPoorAcknowledgeScore.value &&
	detailData.value?.can_portal_login !== true
);

/** Purchasing History Confirm Proxy: editable optional reason + upload (Excellent/Good/Average, Sent, non-portal). */
const showPurchasingHistoryAckInput = computed(() => {
	if (!isUserPurchasing.value || detailEntrySource.value !== 'history' || !detailData.value) return false;
	if (detailData.value.status !== 'B') return false;
	if (detailData.value.can_portal_login === true) return false;
	const grade = resolveScoreGrade(detailData.value.supplier_score, detailData.value.total_result);
	return grade === 'EXCELLENT' || grade === 'GOOD' || grade === 'AVERAGE';
});

/** Purchasing History Completed: reason/attachment visible but not editable. */
const showPurchasingAckReadonly = computed(() =>
	isUserPurchasing.value &&
	detailEntrySource.value === 'history' &&
	detailData.value?.status === 'P' &&
	Boolean(
		detailData.value?.acknowledge_reason ||
		detailData.value?.acknowledge_attachment_original_name
	)
);

const showAckReasonReadonly = computed(() =>
	Boolean(detailData.value?.acknowledge_reason) &&
	(isUserSupplier.value || showPurchasingAckReadonly.value)
);

const showAckAttachmentReadonly = computed(() =>
	Boolean(detailData.value?.acknowledge_attachment_original_name) &&
	(isUserSupplier.value || showPurchasingAckReadonly.value)
);

const canConfirmFromHistory = computed(() => {
	if (!isUserPurchasing.value || !detailData.value) return false;
	if (detailEntrySource.value !== 'history' && detailEntrySource.value !== 'pending-capa-proxy') return false;
	if (detailData.value.status !== 'B') return false;
	if (detailData.value.can_portal_login) return false;
	if (detailData.value.can_purchasing_confirm === true) return true;
	const grade = resolveScoreGrade(detailData.value.supplier_score, detailData.value.total_result);
	return grade === 'EXCELLENT' || grade === 'GOOD' || grade === 'AVERAGE';
});

const canCompleteCapaProxy = computed(() =>
	isUserPurchasing.value &&
	detailEntrySource.value === 'pending-capa-proxy' &&
	detailData.value?.status === 'B' &&
	isPoorAcknowledgeScore.value &&
	detailData.value?.can_portal_login !== true
);

const canSubmitCapaProxy = computed(() =>
	canCompleteCapaProxy.value &&
	acknowledgeReasonWordCount.value >= ACKNOWLEDGE_REASON_MIN_LENGTH
);

const canDismissDetail = computed(() => {
	if (!isUserPurchasing.value || !detailData.value) return false;
	if (detailData.value.status !== 'O' && detailData.value.status !== 'R') return false;
	const grade = resolveScoreGrade(detailData.value.supplier_score, detailData.value.total_result);
	return grade !== 'EXCELLENT';
});

const showDismissDetailSection = computed(() => {
	if (!detailData.value) return false;
	const status = String(detailData.value.status || '').toUpperCase();
	if (status !== 'PD' && status !== 'D') return false;
	return Boolean(
		detailData.value.dismiss_approve_reason ||
		detailData.value.dismiss_approve_attachment_original_name ||
		detailData.value.dismiss_approve_attachment_file
	);
});

const MONTHS_SHORT_EN = Object.freeze([
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
	'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]);

const formatDismissDetailDate = (value) => {
	if (value == null || value === '') return '-';
	const raw = String(value).trim().replace('T', ' ').substring(0, 19);
	const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!match) return String(value);
	const day = String(Number(match[3])).padStart(2, '0');
	const monthIndex = Number(match[2]) - 1;
	if (monthIndex < 0 || monthIndex > 11) return String(value);
	return `${day} ${MONTHS_SHORT_EN[monthIndex]} ${match[1]}`;
};

const detailVendorName = computed(() => {
	if (!detailData.value) return '';
	const supplierRow = detailData.value.details?.find((row) => row.supplier_name);
	return supplierRow?.supplier_name || detailData.value.vendor_id || '';
});

const detailCompanyName = computed(() => company.value?.name || '');

const statusLabel = (status) => {
	const map = { O: 'Outstanding', B: 'Sent', P: 'Completed', R: 'Rejected', D: 'Dismissed', PD: 'Pending Dismiss' };
	return map[status] || status;
};

const categorySeverity = (cat) => {
	const map = { RM: 'danger', PM: 'warning', FG: 'success' };
	return map[cat] || 'info';
};

// const toggleCategory = (cat) => {
// 	const idx = selectedCategories.value.indexOf(cat);
// 	if (idx >= 0) {
// 		selectedCategories.value.splice(idx, 1);
// 	} else {
// 		selectedCategories.value.push(cat);
// 	}
// };

const MONTH_ORDER = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const SEMESTER_MONTHS = {
	1: MONTH_ORDER.slice(0, 6),
	2: MONTH_ORDER.slice(6, 12),
};

const MONTH_NAME_TO_CODE = {
	january: 'JAN',
	february: 'FEB',
	march: 'MAR',
	april: 'APR',
	may: 'MAY',
	june: 'JUN',
	july: 'JUL',
	august: 'AUG',
	september: 'SEP',
	october: 'OCT',
	november: 'NOV',
	december: 'DEC',
	jan: 'JAN',
	feb: 'FEB',
	mar: 'MAR',
	apr: 'APR',
	jun: 'JUN',
	jul: 'JUL',
	aug: 'AUG',
	sep: 'SEP',
	oct: 'OCT',
	nov: 'NOV',
	dec: 'DEC',
};

const normalizeMonthKey = (key) => {
	if (key == null || key === '') return null;

	const raw = String(key).trim();
	const lowered = raw.toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ');

	if (MONTH_NAME_TO_CODE[lowered]) {
		return MONTH_NAME_TO_CODE[lowered];
	}

	const upper = raw.toUpperCase();
	if (MONTH_ORDER.includes(upper)) {
		return upper;
	}

	const short = upper.slice(0, 3);
	if (MONTH_ORDER.includes(short)) {
		return short;
	}

	const num = parseInt(raw, 10);
	if (!Number.isNaN(num) && num >= 1 && num <= 12) {
		return MONTH_ORDER[num - 1];
	}

	return null;
};

const resolveDetailMonths = (semester) => SEMESTER_MONTHS[semester] || MONTH_ORDER.slice(0, 6);

const parseMonthList = (monthList) => {
	if (!monthList) return {};
	if (typeof monthList === 'object' && !Array.isArray(monthList)) {
		const result = {};
		Object.entries(monthList).forEach(([key, value]) => {
			const normalized = normalizeMonthKey(key);
			if (normalized) result[normalized] = value;
		});
		return result;
	}

	let data = monthList;
	if (typeof data === 'string') {
		try {
			data = JSON.parse(data.replace(/'/g, '"'));
		} catch (e) {
			return {};
		}
	}

	if (!Array.isArray(data)) return {};

	const result = {};
	data.forEach((item) => {
		const key = Object.keys(item)[0];
		const normalized = normalizeMonthKey(key);
		if (normalized) {
			result[normalized] = item[key];
		}
	});
	return result;
};

const monthColumns = computed(() => {
	if (!detailData.value) return [];
	return resolveDetailMonths(detailData.value.semester);
});

// Dialog height — mengikuti jumlah baris tabel detail
const detailDialogStyle = computed(() => {
	const rows = flatDetailRows.value.length;
	// komponen tinggi (px): hero ~96 | stats ~78 | tbl-header ~38 | per-row ~42 | section-title ~36 | body-padding ~40 | footer ~64 | extra ~20
	const TABLE_HEADER = 38;
	const PER_ROW = 42;
	const BASE = 96 + 78 + TABLE_HEADER + 36 + 40 + 64 + 20; // ≈ 372
	const hasAck = (showSupplierAckInput.value || showPurchasingProxyAckInput.value) ? 200 : 0;
	const ackRO =
		(showAckReasonReadonly.value ? 88 : 0) +
		(showAckAttachmentReadonly.value ? 88 : 0);
	const raw = BASE + rows * PER_ROW + hasAck + ackRO;
	const maxH = Math.floor(window.innerHeight * 0.92);
	const height = Math.min(Math.max(raw, 420), maxH);
	return { width: '96vw', height: height + 'px', padding: '0' };
});

const flatDetailRows = computed(() => {
	if (!detailData.value || !detailData.value.details) return [];
	const months = monthColumns.value;

	return detailData.value.details.map((row) => {
		const parsed = parseMonthList(row.month_list);
		const monthValues = {};

		months.forEach((month) => {
			const value = parsed[month];
			monthValues[month] = value == null || value === '' ? null : value;
		});

		return {
			category: row.category,
			receiving_method_name: row.receiving_method_name,
			method: row.method,
			supplier_name: row.supplier_name,
			...monthValues,
		};
	});
});

onMounted(async () => {
	const resAuth = await AuthService.whoAmi();
	currentUser.value = resAuth.data.data;

	if (
		currentUser.value.leveltenant != 1 &&
		currentUser.value.leveltenant != 2 &&
		currentUser.value.leveltenant != 3 &&
		currentUser.value.userid !== 'dexaadmin'
	) {
		router.push('/dashboard');
		return;
	}

	if (currentUser.value.leveltenant == 2) {
		await getCompany();
		await getVendorId();
	} else {
		await getCompany();
	}

	const openScoringId = route.query.open;
	if (openScoringId && company.value) {
		openDetail(openScoringId);
	}
});

const getCompany = async () => {
	if (currentUser.value.userid === 'dexaadmin') {
		const companyRes = await LovService.getValueType('COMPANY');
		companyList.value = companyRes.data.data;
	} else if (currentUser.value.leveltenant == 2) {
		const payload = currentUser.value.id;
		const companySuppRes = await PoFulfillment.getCompanySupp(payload);
		try {
			const companySupp = companySuppRes.data.data;
			const companyValues = await Promise.all(
				companySupp.map(async (element) => {
					const res = await LovService.getCompanyByName(element.company);
					const secretKey = 'some-unique-key';
					const simpleCrypto = new SimpleCrypto(secretKey);
					const decipherText = simpleCrypto.decrypt(sessionStorage.getItem('dropdownCompany'));
					if (res.data.data[0].name === decipherText) {
						company.value = res.data.data[0];
					}
					return res.data.data[0];
				})
			);
			companyList.value = companyValues;
		} catch (e) {
			if (e !== 'Break') console.log(e);
		}
	} else if (currentUser.value.leveltenant == 1 || currentUser.value.leveltenant == 3) {
		const res = await LovService.getcompanydb(currentUser.value.bioaddress);
		companyList.value = res.data.data;
		try {
			const secretKey = 'some-unique-key';
			const simpleCrypto = new SimpleCrypto(secretKey);
			const decipherText = simpleCrypto.decrypt(sessionStorage.getItem('dropdownCompany'));
			companyList.value.forEach((element) => {
				if (element.name === decipherText) {
					company.value = element;
					throw 'Break';
				}
			});
		} catch (e) {
			if (e !== 'Break') console.log(e);
		}
	}
};

const getVendorId = async () => {
	if (!company.value?.db_instance) return;

	const resVendorName = await PoFulfillment.getSuppBindComp(currentUser.value.id);
	const payloadSuppVal = { vendorName: resVendorName.data.data[0][0].vendorname };
	const resVendorId = await PlanningOrderService.getSuppValue(payloadSuppVal, company.value.db_instance);
	vendorId.value = resVendorId.data.data[0].vendor_id;
};

const onTabChange = (e) => {
	activeTab.value = e.index;
};

const refreshLists = () => {
	outstandingRef.value?.reload();
	pendingCapaRef.value?.reload();
	pendingCapaProxyRef.value?.reload();
	historyRef.value?.reload();
	pendingDismissRef.value?.reload();
	dismissRef.value?.reload();
};

const getActiveExportFilterParams = () => {
	const purchasingTabRefs = {
		1: pendingCapaRef,
		2: pendingCapaProxyRef,
		3: historyRef,
		4: pendingDismissRef,
		5: dismissRef,
	};

	const activeRef = purchasingTabRefs[activeTab.value]?.value;
	if (activeRef?.getActiveFilters) {
		return activeRef.getActiveFilters() || {};
	}

	const fallbacks = [
		historyRef.value,
		pendingCapaRef.value,
		pendingCapaProxyRef.value,
		pendingDismissRef.value,
		dismissRef.value,
	];
	for (const tab of fallbacks) {
		if (tab?.getActiveFilters) {
			const filters = tab.getActiveFilters();
			if (filters && Object.keys(filters).length) return filters;
		}
	}
	return {};
};

const exportToExcel = async () => {
	if (!isUserPurchasing.value) {
		toast.add({ severity: 'warn', summary: 'Export Excel is available for Purchasing only', life: 3000 });
		return;
	}
	if (!company.value?.value || !company.value?.db_instance || !currentUser.value) {
		toast.add({ severity: 'error', summary: 'Please select Company first', life: 3000 });
		return;
	}

	exporting.value = true;
	try {
		const baseParams = {
			org_id: company.value.value,
			db_instance: company.value.db_instance,
			leveltenant: currentUser.value.leveltenant,
		};
		const filterParams = getActiveExportFilterParams();
		const rows = await collectSpeV2ExportRows({
			service: SpeV2Service,
			baseParams,
			filterParams,
		});
		const result = exportSpeV2RowsToExcel(rows);
		if (result.empty) {
			toast.add({ severity: 'warn', summary: 'Data is Empty', life: 3000 });
		} else {
			toast.add({
				severity: 'success',
				summary: `Exported ${result.count} row(s)`,
				life: 3000,
			});
		}
	} catch (err) {
		console.error('SpeV2Main:exportToExcel', err);
		toast.add({ severity: 'error', summary: 'Failed to export Excel', life: 3000 });
	} finally {
		exporting.value = false;
	}
};

const buildDetailParams = () => {
	const params = {
		org_id: company.value.value,
		db_instance: company.value.db_instance,
		leveltenant: currentUser.value?.leveltenant,
	};

	if (isUserSupplier.value && vendorId.value) {
		params.vendor_id = vendorId.value;
	}

	return params;
};

const openDetail = async (scoringId, source = '') => {
	showDetail.value = true;
	detailLoading.value = true;
	detailData.value = null;
	detailEntrySource.value = source || '';
	selectedCategories.value = [];
	acknowledgeReason.value = '';
	acknowledgeFile.value = null;
	acknowledgeFileName.value = '';
	acknowledgeFileUpload.value?.clear();
	showManualEmailDialog.value = false;
	manualEmailTo.value = [];
	manualEmailCc.value = [];
	manualEmailAttachmentName.value = '';
	manualEmailSupplierType.value = 'LOCAL';
	clearPdfReview();
	showPdfReview.value = false;

	try {
		const params = buildDetailParams();
		const res = await SpeV2Service.getDetail(scoringId, params);
		if (res.status === 200) {
			detailData.value = res.data.data;
			detailSemesterLabel.value = 'S' + res.data.data.semester;
			detailStatusLabel.value = statusLabel(res.data.data.status);
			if (res.data.data.category) {
				selectedCategories.value = res.data.data.category.split(',').filter(Boolean);
			}
		}
	} catch (err) {
		console.log(err);
		toast.add({ severity: 'error', summary: 'Failed to load detail', life: 3000 });
	}

	detailLoading.value = false;
};

const handleSubmit = async () => {
	if (!selectedCategories.value.length) {
		toast.add({ severity: 'warn', summary: 'Please select at least one category', life: 3000 });
		return;
	}

	submitting.value = true;
	try {
		const res = await SpeV2Service.submitEvaluation({
			scoringId: detailData.value.scoring_id,
			categories: selectedCategories.value,
			leveltenant: currentUser.value.leveltenant,
			username: currentUser.value.userid || currentUser.value.username || '',
			manualEmail: showManualEmailDialog.value
				? {
					to: normalizeEmailItems(manualEmailTo.value),
					cc: normalizeEmailItems(manualEmailCc.value),
					supplierType: manualEmailSupplierType.value || 'LOCAL',
				}
				: null,
		});

		if (res.status === 200) {
			const wasManualSend = showManualEmailDialog.value;
			showManualEmailDialog.value = false;
			showDetail.value = false;
			toast.add({ severity: 'success', summary: 'Successfully sent to supplier', life: 3000 });
			toast.add({
				severity: 'info',
				summary: wasManualSend
					? 'Routed by score: Bad/Poor → Pending CAPA Proxy; otherwise History'
					: 'Routed by score & portal access to Pending CAPA / Proxy / History',
				life: 4000,
			});
			refreshLists();
		}
	} catch (err) {
		console.log(err);
		if (err?.response?.status === 409 && err?.response?.data?.data?.requiresManualEmail) {
			const responseData = err.response.data.data;
			manualEmailTo.value = [];
			manualEmailCc.value = normalizeEmailItems(currentUser.value?.bioemailactive || []);
			manualEmailAttachmentName.value = responseData.attachmentName || 'SPE_Report.pdf';
			manualEmailSupplierType.value = 'LOCAL';
			showManualEmailDialog.value = true;
			submitting.value = false;
			return;
		}

		const message = err?.response?.data?.message || 'Failed to submit';
		toast.add({ severity: 'error', summary: message, life: 3000 });
	}
	submitting.value = false;
};

const submitManualEmail = async () => {
	manualEmailTo.value = normalizeEmailItems(manualEmailTo.value);
	manualEmailCc.value = normalizeEmailItems(manualEmailCc.value);

	if (!manualEmailTo.value.length) {
		toast.add({ severity: 'warn', summary: 'Email To wajib diisi', life: 3000 });
		return;
	}

	if (!manualEmailCc.value.length) {
		toast.add({ severity: 'warn', summary: 'Email CC wajib diisi', life: 3000 });
		return;
	}

	if (!manualEmailTo.value.every(isValidEmail) || !manualEmailCc.value.every(isValidEmail)) {
		toast.add({ severity: 'warn', summary: 'Format email tidak valid', life: 3000 });
		return;
	}

	await handleSubmit();
};

const resetDismissForm = () => {
	dismissJustification.value = '';
	dismissFile.value = null;
	dismissFormError.value = '';
};

const onDismissAttachmentError = (errorMessage) => {
	dismissFormError.value = errorMessage || '';
};

const handleDismiss = () => {
	if (!detailData.value?.scoring_id) return;
	resetDismissForm();
	showDismissDialog.value = true;
	nextTick(() => {
		syncSpeV2DialogLayer('.spe-v2-dismiss-dialog', 2000);
	});
};

const submitDismissRequest = async () => {
	if (!detailData.value?.scoring_id) return;

	const justification = String(dismissJustification.value || '').trim();
	if (!justification) {
		dismissFormError.value = 'Justification is required.';
		return;
	}

	const fileCheck = validatePdfAttachment(dismissFile.value);
	if (!fileCheck.ok) {
		dismissFormError.value = fileCheck.error;
		return;
	}

	dismissFormError.value = '';
	dismissing.value = true;
	try {
		const res = await SpeV2Service.dismissEvaluation({
			scoringId: detailData.value.scoring_id,
			leveltenant: currentUser.value.leveltenant,
			id: currentUser.value.id,
			username: currentUser.value.userid,
			justification,
		}, fileCheck.file);
		if (res.status === 200) {
			showDismissDialog.value = false;
			showDetail.value = false;
			toast.add({
				severity: 'success',
				summary: 'Dismiss request submitted — moved to Pending Dismiss',
				life: 3500,
			});
			// Purchasing tabs: Outstanding(0), Pending CAPA(1), Pending CAPA Proxy(2), History(3), Pending Dismiss(4), Dismiss(5)
			if (isUserPurchasing.value) {
				activeTab.value = 4;
			}
			refreshLists();
		}
	} catch (err) {
		console.log(err);
		const message = err?.response?.data?.message || 'Failed to dismiss';
		toast.add({ severity: 'error', summary: message, life: 3000 });
	}
	dismissing.value = false;
};

const handleCompleteCapaProxy = async () => {
	if (!detailData.value?.scoring_id) return;

	const reason = acknowledgeReason.value.trim();
	if (acknowledgeReasonWordCount.value < ACKNOWLEDGE_REASON_MIN_LENGTH) {
		toast.add({
			severity: 'warn',
			summary: `Reason Acknowledge wajib minimal ${ACKNOWLEDGE_REASON_MIN_LENGTH} kata untuk skor Bad / Poor`,
			life: 3500,
		});
		return;
	}

	if (!window.confirm('Complete CAPA by proxy for this supplier (no SPBI account)?')) {
		return;
	}

	completingProxy.value = true;
	try {
		const res = await SpeV2Service.completeCapaProxy({
			scoringId: detailData.value.scoring_id,
			acknowledgeReason: reason,
			leveltenant: currentUser.value.leveltenant,
			username: currentUser.value.userid || currentUser.value.username || '',
		}, acknowledgeFile.value);

		if (res.status === 200) {
			showDetail.value = false;
			toast.add({ severity: 'success', summary: 'CAPA proxy completed', life: 3000 });
			refreshLists();
		}
	} catch (err) {
		console.log(err);
		const message = err?.response?.data?.message || 'Failed to complete CAPA proxy';
		toast.add({ severity: 'error', summary: message, life: 4000 });
	}
	completingProxy.value = false;
};

const handleConfirm = async () => {
	if (!detailData.value?.scoring_id) return;
	if (
		!window.confirm(
			'Confirm (Proxy) this SPE report as Completed for non-SPBI supplier (Excellent / Good / Average)?'
		)
	) {
		return;
	}

	confirming.value = true;
	try {
		const res = await SpeV2Service.confirmEvaluation(
			{
				scoringId: detailData.value.scoring_id,
				leveltenant: currentUser.value.leveltenant,
				username: currentUser.value.userid || currentUser.value.username || '',
				acknowledgeReason: acknowledgeReason.value.trim(),
			},
			acknowledgeFile.value || null
		);

		if (res.status === 200) {
			showDetail.value = false;
			toast.add({ severity: 'success', summary: 'SPE report confirmed (Proxy)', life: 3000 });
			refreshLists();
		}
	} catch (err) {
		console.error('SpeV2Main:handleConfirm', err);
		const message = err?.response?.data?.message || 'Failed to confirm';
		toast.add({ severity: 'error', summary: message, life: 4000 });
	}
	confirming.value = false;
};

const fetchAcknowledgeAttachment = async () => {
	if (!detailData.value?.scoring_id || !detailData.value?.acknowledge_attachment_file) {
		throw new Error('Attachment is not available');
	}

	const res = await SpeV2Service.downloadAcknowledgeAttachment(
		detailData.value.scoring_id,
		buildDetailParams()
	);

	if (res.status !== 200 || !res.data?.data) {
		throw new Error(res.data?.message || 'Failed to load attachment');
	}

	return {
		base64: res.data.data,
		filename: res.data.filename || detailData.value.acknowledge_attachment_original_name || 'attachment.pdf',
		mimeType: res.data.mimeType || detailData.value.acknowledge_attachment_mime_type || 'application/pdf',
	};
};

const handleViewAcknowledgeAttachment = async () => {
	loadingAckAttachment.value = true;
	try {
		const attachment = await fetchAcknowledgeAttachment();
		const blob = await fetch(`data:${attachment.mimeType};base64,${attachment.base64}`).then((r) => r.blob());
		const blobUrl = URL.createObjectURL(blob);
		window.open(blobUrl, '_blank', 'noopener,noreferrer');
		setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
	} catch (err) {
		const message = err?.response?.data?.message || err.message || 'Failed to view attachment';
		toast.add({ severity: 'error', summary: message, life: 3000 });
	}
	loadingAckAttachment.value = false;
};

const handleDownloadAcknowledgeAttachment = async () => {
	loadingAckAttachment.value = true;
	try {
		const attachment = await fetchAcknowledgeAttachment();
		const downloadLink = document.createElement('a');
		downloadLink.href = `data:${attachment.mimeType};base64,${attachment.base64}`;
		downloadLink.download = attachment.filename;
		downloadLink.click();
	} catch (err) {
		const message = err?.response?.data?.message || err.message || 'Failed to download attachment';
		toast.add({ severity: 'error', summary: message, life: 3000 });
	}
	loadingAckAttachment.value = false;
};

const fetchDismissAttachment = async () => {
	if (!detailData.value?.scoring_id || !detailData.value?.dismiss_approve_attachment_file) {
		throw new Error('Supporting document is not available');
	}

	const res = await SpeV2Service.downloadDismissSupportingDocument(
		detailData.value.scoring_id,
		buildDetailParams()
	);

	if (res.status !== 200 || !res.data?.data) {
		throw new Error(res.data?.message || 'Failed to load supporting document');
	}

	return {
		base64: res.data.data,
		filename:
			res.data.filename ||
			detailData.value.dismiss_approve_attachment_original_name ||
			'dismiss-supporting.pdf',
		mimeType:
			res.data.mimeType ||
			detailData.value.dismiss_approve_attachment_mime_type ||
			'application/pdf',
	};
};

const handleViewDismissAttachment = async () => {
	loadingDismissAttachment.value = true;
	try {
		const attachment = await fetchDismissAttachment();
		const blob = await fetch(`data:${attachment.mimeType};base64,${attachment.base64}`).then((r) =>
			r.blob()
		);
		const blobUrl = URL.createObjectURL(blob);
		window.open(blobUrl, '_blank', 'noopener,noreferrer');
		setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
	} catch (err) {
		const message = err?.response?.data?.message || err.message || 'Failed to view supporting document';
		toast.add({ severity: 'error', summary: message, life: 3000 });
	}
	loadingDismissAttachment.value = false;
};

const handleDownloadDismissAttachment = async () => {
	loadingDismissAttachment.value = true;
	try {
		const attachment = await fetchDismissAttachment();
		const downloadLink = document.createElement('a');
		downloadLink.href = `data:${attachment.mimeType};base64,${attachment.base64}`;
		downloadLink.download = attachment.filename;
		downloadLink.click();
	} catch (err) {
		const message =
			err?.response?.data?.message || err.message || 'Failed to download supporting document';
		toast.add({ severity: 'error', summary: message, life: 3000 });
	}
	loadingDismissAttachment.value = false;
};

const onAcknowledgeFileSelect = (event) => {
	acknowledgeFile.value = event.files?.[0] || null;
	acknowledgeFileName.value = acknowledgeFile.value?.name || '';
};

const onAcknowledgeFileRemove = () => {
	acknowledgeFile.value = null;
	acknowledgeFileName.value = '';
};

const handleAcknowledge = async () => {
	const reason = acknowledgeReason.value.trim();

	if (isPoorAcknowledgeScore.value && acknowledgeReasonWordCount.value < ACKNOWLEDGE_REASON_MIN_LENGTH) {
		toast.add({
			severity: 'warn',
			summary: `Reason Acknowledge wajib minimal ${ACKNOWLEDGE_REASON_MIN_LENGTH} kata untuk skor Bad / Poor`,
			life: 3500,
		});
		return;
	}

	acknowledging.value = true;
	try {
		const res = await SpeV2Service.acknowledgeEvaluation({
			scoringId: detailData.value.scoring_id,
			acknowledgeReason: reason,
			leveltenant: currentUser.value.leveltenant,
		}, acknowledgeFile.value);

		if (res.status === 200) {
			toast.add({ severity: 'success', summary: 'SPE Report acknowledged successfully', life: 3000 });
			showDetail.value = false;
			refreshLists();
		}
	} catch (err) {
		console.log(err);
		const message = err?.response?.data?.message || 'Failed to acknowledge';
		toast.add({ severity: 'error', summary: message, life: 3000 });
	}

	acknowledging.value = false;
};

const buildReportParams = () => {
	const params = {
		org_id: company.value.value,
		db_instance: company.value.db_instance,
	};

	if (selectedCategories.value.length) {
		params.category = selectedCategories.value[0];
	} else if (detailData.value?.category) {
		params.category = detailData.value.category.split(',')[0];
	}

	return params;
};

const clearPdfReview = () => {
	pdfReviewUrl.value = '';
	pdfReviewBase64.value = '';
	pdfReviewFileName.value = '';
};

const syncSpeV2DialogLayer = (selector, zIndex) => {
	nextTick(() => {
		const dialog = document.querySelector(`.p-dialog${selector}`);
		if (!dialog) return;

		const mask = dialog.closest('.p-dialog-mask');
		dialog.style.setProperty('z-index', String(zIndex), 'important');
		if (mask) {
			mask.style.setProperty('z-index', String(zIndex - 1), 'important');
		}
	});
};

const buildPdfReviewParams = (scoringId, category) => {
	const params = {
		org_id: company.value.value,
		db_instance: company.value.db_instance,
	};

	if (category) {
		params.category = String(category).split(',')[0];
	} else if (detailData.value?.scoring_id === scoringId && detailData.value?.category) {
		params.category = detailData.value.category.split(',')[0];
	}

	return params;
};

const loadPdfReview = async (scoringId, category) => {
	if (!scoringId || !company.value) return;

	loadingPdfReview.value = true;
	showPdfReview.value = true;
	clearPdfReview();

	try {
		const res = await SpeV2Service.downloadReport(
			scoringId,
			buildPdfReviewParams(scoringId, category)
		);

		if (res.status === 200 && res.data.data) {
			pdfReviewBase64.value = res.data.data;
			pdfReviewFileName.value = res.data.filename || `SPE_Report_${scoringId}.pdf`;
			pdfReviewUrl.value = `data:application/pdf;base64,${res.data.data}`;
			nextTick(() => syncSpeV2DialogLayer('.spe-v2-pdf-review-dialog', 1400));
		} else {
			toast.add({ severity: 'error', summary: 'Failed to load PDF preview', life: 3000 });
			showPdfReview.value = false;
		}
	} catch (err) {
		console.log(err);
		const message = err.response?.data?.message || 'Failed to generate PDF';
		toast.add({ severity: 'error', summary: message, life: 3000 });
		showPdfReview.value = false;
	}

	loadingPdfReview.value = false;
};

const openPdfReview = (scoringId, category) => {
	loadPdfReview(scoringId, category);
};

const handleReviewPdf = async () => {
	if (!detailData.value) return;
	const params = buildReportParams();
	await loadPdfReview(detailData.value.scoring_id, params.category);
};

const handleDownloadPdfFile = () => {
	if (!pdfReviewBase64.value) return;

	const downloadLink = document.createElement('a');
	downloadLink.href = `data:application/pdf;base64,${pdfReviewBase64.value}`;
	downloadLink.download = pdfReviewFileName.value || 'SPE_Report.pdf';
	downloadLink.click();
	toast.add({ severity: 'success', summary: 'PDF downloaded successfully', life: 3000 });
};
</script>

<style scoped>
.spe-v2 .p-tabview-nav {
	border-bottom: 1px solid #dee2e6;
}

.spe-v2 :deep(.p-tabview-panels) {
	padding: 0;
}

.spe-v2 :deep(.spe-v2-table-body) {
	padding: 24px;
}

.spe-v2 :deep(.spe-v2-datatable) {
	width: 100%;
}

.spe-v2 :deep(.spe-v2-datatable .p-datatable-wrapper) {
	overflow-x: auto;
}

.spe-v2 :deep(.spe-v2-datatable .p-datatable-table) {
	table-layout: fixed !important;
	width: 100% !important;
	min-width: 84.5rem !important;
}

.spe-v2 :deep(.spe-v2-datatable--selectable .p-datatable-tbody > tr) {
	cursor: pointer;
}

.spe-v2 :deep(.spe-v2-datatable--selectable .p-datatable-tbody > tr:hover) {
	background: var(--surface-hover) !important;
}

.spe-v2 :deep(.spe-v2-datatable--selectable .p-datatable-tbody > tr.p-highlight) {
	background: #f5c4c4 !important;
	color: #555555 !important;
}

.spe-v2 :deep(.spe-v2-datatable--with-select .p-datatable-table) {
	min-width: 87.5rem !important;
}

.spe-v2 :deep(.spe-v2-datatable .p-datatable-thead > tr > th),
.spe-v2 :deep(.spe-v2-datatable .p-datatable-tbody > tr > td),
.spe-v2 :deep(.spe-v2-datatable .p-datatable-tfoot > tr > td) {
	padding: 0.75rem 0.85rem;
	white-space: normal;
	word-break: break-word;
	overflow-wrap: anywhere;
	vertical-align: top;
}

.spe-v2 :deep(.spe-v2-col-select) {
	width: 5% !important;
	min-width: 5% !important;
	max-width: 5% !important;
}

.spe-v2 :deep(.spe-v2-col-year) {
	width: 6% !important;
	min-width: 6% !important;
	max-width: 6% !important;
}

.spe-v2 :deep(.spe-v2-col-semester) {
	width: 8% !important;
	min-width: 8% !important;
	max-width: 8% !important;
}

.spe-v2 :deep(.spe-v2-col-supplier) {
	width: 18% !important;
	min-width: 18% !important;
	max-width: 18% !important;
}

.spe-v2 :deep(.spe-v2-col-category) {
	width: 7% !important;
	min-width: 7% !important;
	max-width: 7% !important;
}

.spe-v2 :deep(.spe-v2-col-method) {
	width: 13% !important;
	min-width: 13% !important;
	max-width: 13% !important;
}

.spe-v2 :deep(.spe-v2-col-score) {
	width: 10% !important;
	min-width: 10% !important;
	max-width: 10% !important;
}

.spe-v2 :deep(.spe-v2-col-percent) {
	width: 7% !important;
	min-width: 7% !important;
	max-width: 7% !important;
}

.spe-v2 :deep(.spe-v2-col-result) {
	width: 10% !important;
	min-width: 10% !important;
	max-width: 10% !important;
}

.spe-v2 :deep(.spe-v2-col-status) {
	width: 12% !important;
	min-width: 12% !important;
	max-width: 12% !important;
}

.spe-v2 :deep(.spe-v2-col-actions) {
	width: 9% !important;
	min-width: 9% !important;
	max-width: 9% !important;
	text-align: center;
	white-space: nowrap;
}

/* Frozen Actions: opaque bg + z-index (header di atas body/content) */
.spe-v2 :deep(.spe-v2-datatable.p-datatable-scrollable .p-datatable-thead > tr > th) {
	position: sticky;
	top: 0;
	z-index: 2;
}

.spe-v2 :deep(.spe-v2-datatable.p-datatable-scrollable td.p-frozen-column) {
	position: sticky !important;
	background: #fff !important;
	z-index: 3 !important;
	box-shadow: -6px 0 8px -4px rgba(0, 0, 0, 0.1);
}

.spe-v2 :deep(.spe-v2-datatable.p-datatable-scrollable th.p-frozen-column) {
	position: sticky !important;
	top: 0;
	background: #ffecec !important;
	z-index: 6 !important;
	box-shadow: -6px 0 8px -4px rgba(0, 0, 0, 0.1);
}

.spe-v2 :deep(.spe-v2-datatable .p-datatable-tbody > tr > td:not(.p-frozen-column)) {
	position: relative;
	z-index: 0;
}

.spe-v2 :deep(.spe-v2-datatable--selectable .p-datatable-tbody > tr:hover:not(.p-highlight) > td.p-frozen-column) {
	background: #f6f9fc !important;
}

/* Row terpilih: warnai tiap td (bukan hanya tr) supaya frozen tetap opaque */
.spe-v2 :deep(.spe-v2-datatable--selectable .p-datatable-tbody > tr.p-highlight > td) {
	background: #f5c4c4 !important;
}

.spe-v2 :deep(.spe-v2-datatable--selectable .p-datatable-tbody > tr.p-highlight > td.p-frozen-column) {
	background: #f5c4c4 !important;
	z-index: 4 !important;
}

.spe-v2 :deep(.spe-v2-datatable--selectable .p-datatable-tbody > tr.p-highlight > td:not(.p-frozen-column)) {
	z-index: 0 !important;
}

.spe-v2 :deep(.spe-v2-datatable .p-datatable-frozen-view .p-datatable-thead > tr > th) {
	background: #ffecec;
}

.spe-v2 :deep(.spe-v2-datatable .p-datatable-frozen-view .p-datatable-tbody > tr > td) {
	background: #fff;
}

.spe-v2-pdf-preview {
	width: 100%;
	height: 75vh;
	border: 1px solid #dee2e6;
	border-radius: 4px;
}

.spe-v2 :deep(.spe-v2-cell-wrap) {
	display: block;
	white-space: normal;
	word-break: break-word;
}

.spe-v2 :deep(.spe-v2-detail-datatable .p-datatable-wrapper) {
	overflow-x: auto;
}

.spe-v2 :deep(.spe-v2-detail-datatable .p-datatable-table) {
	table-layout: fixed !important;
	width: 100% !important;
}

.spe-v2 :deep(.spe-v2-detail-datatable .p-datatable-thead > tr > th),
.spe-v2 :deep(.spe-v2-detail-datatable .p-datatable-tbody > tr > td) {
	padding: 0.65rem 0.75rem;
	white-space: normal;
	word-break: break-word;
	overflow-wrap: anywhere;
	vertical-align: top;
}

.spe-v2 :deep(.spe-v2-detail-col-category) {
	width: 5rem;
	max-width: 5rem;
}

.spe-v2 :deep(.spe-v2-detail-col-receiving) {
	width: 9rem;
	max-width: 9rem;
}

.spe-v2 :deep(.spe-v2-detail-col-method) {
	width: 8%;
	max-width: 8%;
}

.spe-v2 :deep(.spe-v2-detail-col-supplier) {
	width: 20%;
	max-width: 20%;
}

.spe-v2 :deep(.spe-v2-detail-col-month) {
	width: 4.5%;
	max-width: 4.5%;
	text-align: center;
}

.spe-v2 :deep(.spe-v2-datatable .p-datatable-scrollable-header),
.spe-v2 :deep(.spe-v2-datatable .p-datatable-scrollable-body),
.spe-v2 :deep(.spe-v2-datatable .p-datatable-scrollable-footer),
.spe-v2 :deep(.spe-v2-datatable .p-datatable-frozen-view),
.spe-v2 :deep(.spe-v2-datatable .p-datatable-scrollable-view),
.spe-v2 :deep(.spe-v2-datatable .p-datatable-scrollable-view .p-datatable-scrollable-body) {
	background: #fff;
	overflow-x: auto;
}

.category-chip {
	display: inline-flex;
	align-items: center;
	padding: 0.5rem 1.25rem;
	border: 2px solid #dee2e6;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.2s ease;
	background: #fff;
	color: #6c757d;
	min-width: 80px;
	justify-content: center;
}

.category-chip:hover {
	border-color: #3b82f6;
	color: #3b82f6;
}

.category-chip--selected {
	border-color: #3b82f6;
	background: #eff6ff;
	color: #3b82f6;
}

.category-chip--selected i {
	color: #3b82f6;
}

/* ── Detail Dialog shell ── */
.spe-detail-shell {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow: hidden;
	border-radius: 8px;
}

.spe-detail-shell .spe-detail-stats,
.spe-detail-shell .spe-detail-body,
.spe-detail-shell .spe-detail-acknowledge,
.spe-detail-shell .spe-detail-acknowledge-readonly {
	flex-shrink: 0;
}

/* area tengah yang bisa scroll */
.spe-detail-scroll {
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
}

/* ── Hero header ── */
.spe-detail-hero {
	position: relative;
	display: flex;
	align-items: center;
	gap: 1.5rem;
	padding: 1rem 3.5rem 1rem 1.5rem;
	background: #fff;
	border-bottom: 3px solid #da3831;
	flex-shrink: 0;
}

.spe-detail-hero__left {
	flex: 1;
	min-width: 0;
}

.spe-detail-hero__vendor {
	font-size: 1.1rem;
	font-weight: 700;
	color: #1a1a2e;
	margin-bottom: 0.5rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.spe-detail-hero__meta {
	display: flex;
	flex-wrap: wrap;
	gap: 0.4rem;
}

.spe-detail-meta-badge {
	display: inline-flex;
	align-items: center;
	background: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 20px;
	padding: 3px 10px;
	font-size: 0.72rem;
	font-weight: 600;
	color: #495057;
	gap: 4px;
}

/* Score box */
.spe-detail-score-ring {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-width: 100px;
	padding: 0.6rem 1rem;
	border-radius: 10px;
	border: 2.5px solid #da3831;
	background: linear-gradient(135deg, #fff5f5 0%, #fff 100%);
	flex-shrink: 0;
	gap: 2px;
	box-shadow: 0 2px 8px rgba(218, 56, 49, 0.12);
}

.spe-detail-score-ring__value {
	font-size: 1.35rem;
	font-weight: 800;
	line-height: 1.15;
	color: #da3831;
	text-align: center;
	word-break: break-word;
	max-width: 120px;
}

.spe-detail-score-ring__label {
	font-size: 0.62rem;
	color: #da3831;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	font-weight: 700;
	text-align: center;
	opacity: 0.8;
}

.spe-detail-score-ring.chip-score-excellent {
	border-color: #10b981;
	background: linear-gradient(135deg, #ecfdf5 0%, #fff 100%);
	box-shadow: 0 2px 8px rgba(16, 185, 129, 0.16);
}

.spe-detail-score-ring.chip-score-excellent .spe-detail-score-ring__value,
.spe-detail-score-ring.chip-score-excellent .spe-detail-score-ring__label {
	color: #10b981;
}

.spe-detail-score-ring.chip-score-good {
	border-color: #3b82f6;
	background: linear-gradient(135deg, #eff6ff 0%, #fff 100%);
	box-shadow: 0 2px 8px rgba(59, 130, 246, 0.16);
}

.spe-detail-score-ring.chip-score-good .spe-detail-score-ring__value,
.spe-detail-score-ring.chip-score-good .spe-detail-score-ring__label {
	color: #3b82f6;
}

.spe-detail-score-ring.chip-score-average {
	border-color: #ff9800;
	background: linear-gradient(135deg, #fff7ed 0%, #fff 100%);
	box-shadow: 0 2px 8px rgba(255, 152, 0, 0.18);
}

.spe-detail-score-ring.chip-score-average .spe-detail-score-ring__value,
.spe-detail-score-ring.chip-score-average .spe-detail-score-ring__label {
	color: #ff9800;
}

.spe-detail-score-ring.chip-score-bad {
	border-color: #f59e0b;
	background: linear-gradient(135deg, #fffbeb 0%, #fff 100%);
	box-shadow: 0 2px 8px rgba(245, 158, 11, 0.18);
}

.spe-detail-score-ring.chip-score-bad .spe-detail-score-ring__value,
.spe-detail-score-ring.chip-score-bad .spe-detail-score-ring__label {
	color: #b45309;
}

.spe-detail-score-ring.chip-score-poor {
	border-color: #ef4444;
	background: linear-gradient(135deg, #fef2f2 0%, #fff 100%);
	box-shadow: 0 2px 8px rgba(239, 68, 68, 0.16);
}

.spe-detail-score-ring.chip-score-poor .spe-detail-score-ring__value,
.spe-detail-score-ring.chip-score-poor .spe-detail-score-ring__label {
	color: #ef4444;
}

/* Status pill */
.spe-detail-status-pill {
	display: inline-flex;
	align-items: center;
	border-radius: 20px;
	padding: 5px 14px;
	font-size: 0.78rem;
	font-weight: 700;
	gap: 5px;
	white-space: nowrap;
}

.spe-detail-status-pill--O {
	background: #e8f0fe;
	color: #1a56db;
	border: 1.5px solid #a4c2f4;
}

.spe-detail-status-pill--B {
	background: #fffbeb;
	color: #92400e;
	border: 1.5px solid #fcd34d;
}

.spe-detail-status-pill--P {
	background: #ecfdf5;
	color: #065f46;
	border: 1.5px solid #6ee7b7;
}

.spe-detail-status-pill--R {
	background: #fff1f2;
	color: #be123c;
	border: 1.5px solid #fda4af;
}

/* Close button */
.spe-detail-hero__close {
	position: absolute;
	top: 0.75rem;
	right: 0.85rem;
	background: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 50%;
	width: 30px;
	height: 30px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.15s;
	font-size: 0.8rem;
}

.spe-detail-hero__close:hover {
	background: #fee2e2;
	color: #da3831;
	border-color: #fca5a5;
}

/* ── Stat cards row ── */
.spe-detail-stats {
	display: flex;
	flex-wrap: nowrap;
	gap: 0;
	background: #f8f9fb;
	border-bottom: 1px solid #e5e8ef;
	flex-shrink: 0;
	overflow-x: auto;
}

.spe-detail-stat-card {
	flex: 1;
	min-width: 100px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 0.75rem 1rem;
	border-right: 1px solid #e5e8ef;
	transition: background 0.15s;
}

.spe-detail-stat-card:last-child {
	border-right: none;
}

.spe-detail-stat-card__label {
	font-size: 0.65rem;
	color: #8a94a6;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	font-weight: 700;
	margin-bottom: 0.25rem;
	text-align: center;
}

.spe-detail-stat-card__value {
	font-size: 1.05rem;
	font-weight: 700;
	color: #1a1a2e;
}

.spe-detail-stat-card__value--primary {
	color: #da3831;
	font-size: 1.2rem;
}

/* ── Body ── */
.spe-detail-body {
	padding: 1.25rem 1.5rem;
}

.spe-detail-section-title {
	font-size: 0.85rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #64748b;
	margin-bottom: 0.75rem;
}

/* ── Footer ── */
.spe-detail-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0.85rem 1.5rem;
	border-top: 1px solid #e2e8f0;
	background: #fff;
	flex-wrap: wrap;
	gap: 0.5rem;
}

.spe-detail-footer__left,
.spe-detail-footer__right {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.spe-detail-acknowledge,
.spe-detail-acknowledge-readonly {
	padding: 1rem 1.25rem 0;
}

.spe-detail-acknowledge__label {
	display: block;
	font-weight: 600;
	margin-bottom: 0.5rem;
}

.spe-detail-acknowledge__required {
	color: #da3831;
}

.spe-detail-acknowledge__optional {
	font-weight: 400;
	color: var(--text-color-secondary);
	font-size: 0.9em;
}

.spe-detail-acknowledge__hint {
	display: block;
	margin-top: 0.35rem;
	color: var(--text-color-secondary);
}

.spe-detail-acknowledge-readonly__text {
	margin: 0;
	padding: 0.75rem 1rem;
	background: #f8f9fa;
	border: 1px solid #e9ecef;
	border-radius: 6px;
	white-space: pre-wrap;
	word-break: break-word;
}

.spe-detail-acknowledge-readonly__text--inline {
	display: inline-block;
	margin: 0;
}
</style>

<style>
/* Header sticky DataTable menembus modal jika tidak di-reset saat overlay aktif */
body.p-overflow-hidden .spe-v2-datatable.p-datatable-scrollable .p-datatable-thead,
body.p-overflow-hidden .spe-v2-datatable.p-datatable-scrollable th.p-frozen-column,
body.p-overflow-hidden .spe-v2-datatable.p-datatable-scrollable td.p-frozen-column,
body.p-overflow-hidden .spe-v2-datatable.p-datatable-scrollable th.spe-v2-col-actions,
body.p-overflow-hidden .spe-v2-datatable.p-datatable-scrollable td.spe-v2-col-actions,
body.p-overflow-hidden .spe-v2-datatable .p-datatable-frozen-view .p-datatable-thead {
	position: relative !important;
	top: auto !important;
	right: auto !important;
	z-index: 0 !important;
	box-shadow: none !important;
}

/* Keep Search Results below filter LOV overlays (Year/Semester/Supplier/etc.) */
.spe-v2-results {
	position: relative;
	z-index: 0;
	isolation: isolate;
}

/* Detail dialog — full height fill */
.spe-v2-detail-dialog.p-dialog {
	display: flex;
	flex-direction: column;
}

.spe-v2-detail-dialog .p-dialog-content {
	flex: 1;
	overflow: hidden;
	padding: 0 !important;
	display: flex;
	flex-direction: column;
}
</style>
